import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { gql, useMutation } from "@apollo/client";
import { signIn } from "../../utils/authHelpers";

const CREATE_PROFILE = gql`
  mutation CreateProfile(
    $firstName: String!
    $lastName: String!
    $ageGroup: AgeGroupEnum!
  ) {
    createProfile(
      input: { firstName: $firstName, lastName: $lastName, ageGroup: $ageGroup }
    )
  }
`;

const SignupPage = ({ setIsAuthenticated }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [ageGroup, setAgeGroup] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [createProfile] = useMutation(CREATE_PROFILE);

  // Restore state if returning from SSO
  useEffect(() => {
    if (location.state) {
      const { firstName: f, lastName: l, ageGroup: a, fromSSO } = location.state;
      if (f) setFirstName(f);
      if (l) setLastName(l);
      if (a) setAgeGroup(a);

      if (fromSSO) {
        const idToken = localStorage.getItem("idToken");
        if (idToken) {
          try {
            const payload = JSON.parse(atob(idToken.split(".")[1]));
            if (payload.given_name) setFirstName(payload.given_name);
            if (payload.family_name) setLastName(payload.family_name);
          } catch (err) {
            console.warn("Failed to parse ID token:", err);
          }
        }
      }
    }
  }, [location.state]);

  const handleSignup = async (e) => {
    e.preventDefault();
    setError("");

    if (!firstName || !lastName || !ageGroup) {
      setError("Please fill in all required fields.");
      return;
    }

    setLoading(true);

    try {
      // SSO users: assume session exists
      if (!location.state?.fromSSO) {
        const email = localStorage.getItem("email");
        const password = localStorage.getItem("password");
        if (!email || !password) {
          setError("Missing signup credentials. Please start over.");
          setLoading(false);
          return;
        }

        await signIn(email, password);
        setIsAuthenticated(true);

        localStorage.removeItem("email");
        localStorage.removeItem("password");
      } else {
        setIsAuthenticated(true);
      }

      // Create profile
      const formatted =
        ageGroup === "86_plus"
          ? "OVER_85"
          : `AGE_${ageGroup.replace("-", "_")}`.toUpperCase();

      const { data } = await createProfile({
        variables: { firstName, lastName, ageGroup: formatted },
      });

      if (data?.createProfile) {
        navigate("/terms"); // navigate to terms page
      } else {
        setError("Could not create profile. Please try again.");
      }
    } catch (err) {
      console.error("Signup error:", err);
      setError(err.message || "An error occurred during profile creation.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-[#2F2D2D] text-white">
      <div className="w-full max-w-xl p-8 space-y-6 bg-[#2F2D2D] rounded-2xl shadow-lg relative">
        <h1 className="text-3xl font-bold text-center">Complete Your Profile</h1>

        {error && <p className="text-red-500 text-center">{error}</p>}

        <form onSubmit={handleSignup} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col">
              <label className="text-[#65DEF6] mb-1">First Name</label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full p-3 bg-gray-800 rounded-lg border border-gray-600 text-white"
                required
              />
            </div>
            <div className="flex flex-col">
              <label className="text-[#65DEF6] mb-1">Last Name</label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="w-full p-3 bg-gray-800 rounded-lg border border-gray-600 text-white"
                required
              />
            </div>
          </div>

          <div className="flex flex-col">
            <label className="text-[#65DEF6] mb-1">Age Group</label>
            <select
              value={ageGroup}
              onChange={(e) => setAgeGroup(e.target.value)}
              className="w-full p-3 bg-gray-800 rounded-lg border border-gray-600 text-white"
              required
            >
              <option value="" disabled>
                Select your age group
              </option>
              <option value="18_25">18-25</option>
              <option value="26_35">26-35</option>
              <option value="36_45">36-45</option>
              <option value="46_55">46-55</option>
              <option value="56_65">56-65</option>
              <option value="66_75">66-75</option>
              <option value="76_85">76-85</option>
              <option value="86_plus">86+</option>
            </select>
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full font-bold py-3 px-4 rounded-lg transition ${
              loading
                ? "bg-gray-500 text-gray-300 cursor-not-allowed"
                : "bg-[#C6E675] hover:bg-[#4C9B8C] text-gray-900"
            }`}
          >
            {loading ? "Processing..." : "Continue"}
          </button>
        </form>

        <img
          src="/assets/images/puppy-2.png"
          alt="Dog Illustration"
          className="absolute w-[230.63px] h-[190.79px] bottom-[-200px] right-[-440px]"
        />
      </div>
    </div>
  );
};

export default SignupPage;
